import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { Car, Clock, Phone } from 'lucide-react'

import { useReservation } from '@/components/ReservationContext'
import { HAIRDRESSING_SERVICES } from '@/components/additional-services'
import { useReservationModal } from '@/pages/panel-interno/components/ReservationModalContext'
import { cn } from '@/shared/lib/styles/class-merge'
import { Badge } from '@/shared/ui/badge'
import { serviceTypeColors } from '@/shared/utils/serviceTypeColors'
import { HairSalonScheduledTask } from '@monorepo/functions/src/types/reservations'
import { HairdressingServiceType } from '@monorepo/functions/src/types/services'

import { getPetFromReservation } from '../model/task-utils'
import { useCalendarStore } from '../model/store'
import { TaskEditionModal } from './TaskEditionModal'
import { isDriverService } from '@/shared/types/service-checkers'

interface TaskCardProps {
    task: HairSalonScheduledTask
    className?: string
    showDetails?: boolean
    isWeekView?: boolean
}

// Minimal info card (no reservation or loading)
type MinimalTaskCardProps = {
    task: HairSalonScheduledTask
    serviceLabel: string
    serviceColor: string
    className?: string
    onDoubleClick: () => void
}
function MinimalTaskCard({ task, serviceLabel, serviceColor, className, onDoubleClick }: MinimalTaskCardProps) {
    return (
        <div
            className={cn('h-full rounded-sm border p-2 transition-colors', 'border-gray-200 bg-gray-50', className)}
            onDoubleClick={e => {
                e.stopPropagation()
                return onDoubleClick()
            }}
        >
            <div className='flex h-full flex-col gap-1'>
                <Badge
                    variant='outline'
                    className={cn('whitespace-nowrap border-[1.5px] px-1.5 py-0 text-[10px]', serviceColor)}
                >
                    {serviceLabel}
                </Badge>
                <div className='mt-auto flex items-center gap-1 text-[11px] text-gray-600'>
                    <Clock className='h-3 w-3 flex-shrink-0' />
                    <span>
                        {task.time} ({task.duration} min)
                    </span>
                </div>
            </div>
        </div>
    )
}

// Week view card (compact)
type WeekViewTaskCardProps = {
    task: HairSalonScheduledTask
    taskPet: any
    reservation: any
    serviceLabel: string
    serviceColor: string
    className?: string
    onDoubleClick: () => void
}
function WeekViewTaskCard({
    task,
    taskPet,
    reservation,
    serviceLabel,
    serviceColor,
    className,
    onDoubleClick,
}: WeekViewTaskCardProps) {
    return (
        <div
            className={cn(
                'h-full overflow-hidden rounded-sm border p-1 transition-colors',
                reservation.type === 'hotel' ? 'border-emerald-200 bg-emerald-100' : 'border-pink-200 bg-pink-100',
                className,
            )}
            onDoubleClick={e => {
                e.stopPropagation()
                return onDoubleClick()
            }}
        >
            <div className='flex h-full flex-col justify-start text-xs'>
                {task.duration > 15 && (
                    <Badge
                        variant='outline'
                        className={cn(
                            'h-fit w-fit max-w-full overflow-hidden truncate whitespace-nowrap border-[1.5px] px-1.5 py-0 text-[10px] font-normal',
                            serviceColor,
                        )}
                    >
                        {serviceLabel}
                    </Badge>
                )}
                <div className='truncate font-bold'>{taskPet.name}</div>
                {task.duration >= 60 && <div className='text-[10px] text-gray-600'>{taskPet.breed}</div>}
            </div>
        </div>
    )
}

// Compact daily view card
type CompactTaskCardProps = {
    taskPet: any
    serviceLabel: string
    serviceColor: string
    className?: string
    onDoubleClick: () => void
}
function CompactTaskCard({ taskPet, serviceLabel, serviceColor, className, onDoubleClick }: CompactTaskCardProps) {
    return (
        <div
            className='flex h-full flex-col gap-1'
            onDoubleClick={e => {
                e.stopPropagation()
                return onDoubleClick()
            }}
        >
            <div className='flex items-center justify-between'>
                <div className='flex-1'>
                    <div className='flex items-center gap-1 text-sm'>
                        <span className='truncate font-bold'>{taskPet.name}</span>
                        <span className='truncate text-[10px] text-gray-600'>({taskPet.breed})</span>
                    </div>
                </div>
                <div className='flex items-center gap-1'>
                    <Badge
                        variant='outline'
                        className={cn('whitespace-nowrap border-[1.5px] px-1.5 py-0 text-[10px]', serviceColor)}
                    >
                        {serviceLabel}
                    </Badge>
                </div>
            </div>
        </div>
    )
}

// Full daily view card
type FullTaskCardProps = {
    task: HairSalonScheduledTask
    taskPet: any
    reservation: any
    serviceLabel: string
    serviceColor: string
    showDetails: boolean
    className?: string
    hasDriverService: boolean
    onDoubleClick: () => void
}
function FullTaskCard({
    task,
    taskPet,
    reservation,
    serviceLabel,
    serviceColor,
    showDetails,
    className,
    hasDriverService,
    onDoubleClick,
}: FullTaskCardProps) {
    return (
        <div
            className='flex h-full flex-col gap-1'
            onDoubleClick={e => {
                e.stopPropagation()
                return onDoubleClick()
            }}
        >
            <div className='flex items-center justify-between'>
                <div className='flex-1'>
                    <div className='flex items-center gap-1 text-sm'>
                        <span className='truncate font-bold'>{taskPet.name}</span>
                        <span className='truncate text-[10px] text-gray-600'>({taskPet.breed})</span>
                    </div>
                </div>
                <Badge
                    variant='outline'
                    className={cn('whitespace-nowrap border-[1.5px] px-1.5 py-0 text-[10px]', serviceColor)}
                >
                    {serviceLabel}
                </Badge>
            </div>
            {showDetails && (
                <>
                    <div className='flex items-center gap-2 text-[11px] text-gray-600'>
                        <div className='flex items-center gap-1'>
                            <Phone className='h-3 w-3 flex-shrink-0' />
                            <span className='truncate'>{reservation.client.phone}</span>
                        </div>
                        <div className='flex items-center gap-1'>
                            <Clock className='h-3 w-3 flex-shrink-0' />
                            <span>
                                {task.time} ({task.duration} min)
                            </span>
                        </div>
                        {hasDriverService && (
                            <div className='flex items-center gap-1'>
                                <Car className='h-3 w-3 flex-shrink-0' />
                                <span>Servicio de transporte</span>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    )
}

export function TaskCard({ task, className, showDetails = true, isWeekView = false }: TaskCardProps) {
    const { t } = useTranslation()
    const { scheduledTasks, setSelectedTask, setScheduledTasks } = useCalendarStore()
    const [searchParams] = useSearchParams()
    const selectedReservationId = searchParams.get('reservationId')
    const { reservation, isLoading: isLoadingReservation, updateReservation } = useReservation(task.reservationId)
    const { openReservationModal } = useReservationModal()
    const [selectedModalTask, setSelectedModalTask] = useState<HairSalonScheduledTask | null>(null)
    const [isTimeSelectionModalOpen, setIsTimeSelectionModalOpen] = useState(false)

    const isSelected = selectedReservationId === task.reservationId

    const handleTaskDoubleClick = (task: HairSalonScheduledTask) => {
        setSelectedModalTask(task)
        setIsTimeSelectionModalOpen(true)
    }

    const handleTaskSave = (hourWithMinutes: string, service: HairdressingServiceType, duration: number) => {
        if (reservation) {
            console.log('TODO: Save task', hourWithMinutes, service, duration)

            updateReservation({
                tasks: reservation.tasks!.map(t =>
                    t.id === task.id
                        ? {
                              ...t,
                              time: hourWithMinutes,
                              service,
                              duration,
                          }
                        : t,
                ),
            })
            setSelectedTask(null as any)
            setScheduledTasks(
                scheduledTasks.map(t => (t.id === task.id ? { ...t, time: hourWithMinutes, service, duration } : t)),
            )
        }
    }

    const handleTaskDelete = () => {
        if (!reservation) return
        console.log('handleTaskDelete', reservation)
        // Remove from reservation
        updateReservation({
            tasks: reservation.tasks!.filter(t => t.id !== task.id),
        })
        // Remove from store
        setScheduledTasks(scheduledTasks.filter(t => t.id !== task.id))
        setSelectedTask(null as any)
    }

    const handleGoToReservation = () => {
        if (reservation) openReservationModal(reservation)
    }

    const taskPet = useMemo(() => {
        if (!reservation) return null
        return getPetFromReservation(reservation, task.petIndex)
    }, [reservation, task.petIndex])

    const serviceLabel = t(HAIRDRESSING_SERVICES.find(service => service.value === task.service)?.translationKey!)
    const serviceColor = serviceTypeColors[task.service]

    // If no reservation found, show minimal info
    if (!reservation || isLoadingReservation || !taskPet) {
        return (
            <>
                <MinimalTaskCard
                    task={task}
                    serviceLabel={serviceLabel}
                    serviceColor={serviceColor}
                    className={cn(className, isSelected && 'border-2 border-yellow-500')}
                    onDoubleClick={() => handleTaskDoubleClick(task)}
                />
                {selectedModalTask && (
                    <TaskEditionModal
                        isOpen={isTimeSelectionModalOpen}
                        onClose={() => setIsTimeSelectionModalOpen(false)}
                        onSave={handleTaskSave}
                        hour={task.time.split(':')[0]}
                        task={selectedModalTask}
                        onDelete={handleTaskDelete}
                    />
                )}
            </>
        )
    }

    if (isWeekView) {
        return (
            <>
                <WeekViewTaskCard
                    task={task}
                    taskPet={taskPet}
                    reservation={reservation}
                    serviceLabel={serviceLabel}
                    serviceColor={serviceColor}
                    className={cn(className, isSelected && 'border-2 border-yellow-600')}
                    onDoubleClick={() => handleTaskDoubleClick(task)}
                />
                {selectedModalTask && (
                    <TaskEditionModal
                        isOpen={isTimeSelectionModalOpen}
                        onClose={() => setIsTimeSelectionModalOpen(false)}
                        onSave={handleTaskSave}
                        hour={task.time.split(':')[0]}
                        task={selectedModalTask}
                        onDelete={handleTaskDelete}
                        onGoToReservation={handleGoToReservation}
                    />
                )}
            </>
        )
    }

    const isCompactView = task.duration < 45
    const is15MinTask = task.duration <= 15

    return (
        <>
            <div
                className={cn(
                    'h-full rounded-sm border transition-colors',
                    is15MinTask ? 'pl-2' : 'p-2',
                    reservation.type === 'hotel' ? 'border-emerald-200 bg-emerald-50' : 'border-pink-200 bg-pink-50',
                    isSelected && 'border-2 border-yellow-500',
                    className,
                )}
                onDoubleClick={() => handleTaskDoubleClick(task)}
            >
                {isCompactView ? (
                    <CompactTaskCard
                        taskPet={taskPet}
                        serviceLabel={serviceLabel}
                        serviceColor={serviceColor}
                        className={className}
                        onDoubleClick={() => handleTaskDoubleClick(task)}
                    />
                ) : (
                    <FullTaskCard
                        task={task}
                        taskPet={taskPet}
                        reservation={reservation}
                        serviceLabel={serviceLabel}
                        serviceColor={serviceColor}
                        showDetails={showDetails}
                        className={className}
                        hasDriverService={reservation.additionalServices.some(isDriverService)}
                        onDoubleClick={() => handleTaskDoubleClick(task)}
                    />
                )}
            </div>
            {selectedModalTask && (
                <TaskEditionModal
                    isOpen={isTimeSelectionModalOpen}
                    onClose={() => setIsTimeSelectionModalOpen(false)}
                    onSave={handleTaskSave}
                    hour={task.time.split(':')[0]}
                    task={selectedModalTask}
                    onDelete={handleTaskDelete}
                    onGoToReservation={handleGoToReservation}
                />
            )}
        </>
    )
}
